import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Overline } from '@/design-system/primitives'
import { UndoPublication } from '@/features/admin/components/UndoPublication'
import {
  CountdownPanel,
  DurationPicker,
  formatClock,
  useCountdown,
} from '@/features/voting/countdown'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'

import { useContestAction, useContestBoard, useOnStage, type VoteAction } from './api'
import type { ConductorRound, ConductorVote, VoteStatus } from './types'

/**
 * Les gestes du scrutin, dans l'ordre où on les fait au micro.
 *
 * `publish` demande un second appui — le seul de tout le conducteur. Le reste
 * se rattrape d'un geste ; une publication par erreur, elle, se retire à part
 * (« Annuler la publication », avec un motif). Il n'apparaît que pour un
 * scrutin isolé : les tours d'un concours se publient (et se retirent)
 * ensemble, depuis « Votes », après la proclamation — jamais d'ici.
 */
const VOTE_STEPS: Record<VoteStatus, VoteAction[]> = {
  closed: ['open'],
  open: ['freeze', 'close'],
  frozen: ['publish', 'open'],
  published: [],
}

const ROUND_TONES = {
  pending: 'neutral',
  running: 'ember',
  done: 'success',
} as const

const VOTE_TONES = {
  closed: 'neutral',
  open: 'ember',
  frozen: 'neutral',
  published: 'champagne',
} as const

function label(value: Record<string, string>, language: string): string {
  return value[language] ?? Object.values(value)[0] ?? '—'
}

/**
 * Le concours, au conducteur — sur un téléphone, d'une main.
 *
 * Un tour démarre de lui-même quand on lance l'étape du déroulé à laquelle il
 * est rattaché. Ce panneau sert au reste : démarrer ou terminer un tour à la
 * main si l'étape n'est pas rattachée, lire qui est sur scène, et conduire le
 * vote de chaque tour — ouvrir, geler — en voyant le compteur de bulletins
 * monter. Jamais le score : il se lit dans « Votes », à l'abri des regards,
 * avant de l'annoncer.
 */
/**
 * Un scrutin et ses gestes : compteur, statut, ouvrir / geler / fermer, et
 * publier (ou retirer) un scrutin isolé.
 *
 * Partagé par le panneau des tours et par l'onglet « Votes » du conducteur :
 * un même vote se conduit de la même façon où qu'on le croise.
 */
export function VoteControlRow({
  eventId,
  vote,
  canVote,
  children,
}: {
  eventId: string | undefined
  vote: ConductorVote
  canVote: boolean
  /** Ce que l'écran appelant ajoute sous les gestes (votants, dépouillement). */
  children?: ReactNode
}) {
  const { t, i18n } = useTranslation()
  const action = useContestAction(eventId)
  // Publier demande un second appui : la seule confirmation du conducteur.
  const [publishing, setPublishing] = useState(false)
  // La durée annoncée à l'ouverture : celle du tour précédent par défaut,
  // pour que le MC n'ait qu'à appuyer sur « Ouvrir le vote ».
  const [duration, setDuration] = useState<number | null>(vote.countdown_seconds ?? null)
  const remaining = useCountdown(
    vote.status === 'open' ? vote.countdown_ends_at : null,
    vote.server_now,
  )
  // Le temps est écoulé : le vote reste ouvert, mais le geste qui le ferme
  // passe au premier plan.
  const elapsed = remaining === 0

  // Un vote sans nommé ne s'ouvre pas (le serveur le refuse) : on le dit
  // plutôt que d'offrir un bouton qui ne mènerait qu'à une erreur.
  const empty = vote.status === 'closed' && vote.candidate_ids.length === 0
  // Sans le droit de conduire les votes, on voit le compteur, pas les boutons.
  const steps = canVote && !empty
    ? VOTE_STEPS[vote.status].filter(
        (step) => !(step === 'publish' && vote.competition_category_id),
      )
    : []

  return (
    <div className="rounded-md border border-line-subtle px-3 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 break-words text-sm text-content-primary">
          {label(vote.name_i18n, i18n.language)}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {vote.status !== 'closed' ? (
            <span className="font-mono text-caption tabular-nums text-content-muted">
              {t('conductor.contest.ballots', { count: vote.ballots })}
            </span>
          ) : null}
          <Badge tone={VOTE_TONES[vote.status]}>
            {t(`conductor.contest.voteStatus.${vote.status}`)}
          </Badge>
        </span>
      </div>

      {publishing ? (
        <div className="mt-3 space-y-2">
          <p className="text-caption text-state-warning">{t('conductor.contest.publishWarning')}</p>
          <div className="grid grid-cols-2 gap-2">
            <Button
              size="sm"
              variant="ember"
              disabled={action.isPending}
              onClick={() =>
                action.mutate(
                  { kind: 'vote', id: vote.id, action: 'publish' },
                  { onSettled: () => setPublishing(false) },
                )
              }
            >
              {t('conductor.contest.publishConfirm')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPublishing(false)}>
              {t('admin.cancel')}
            </Button>
          </div>
        </div>
      ) : canVote && vote.status === 'published' && !vote.competition_category_id ? (
        <UndoPublication
          className="mt-3"
          label={t('admin.undo.voteUnpublish')}
          confirmLabel={t('admin.undo.voteUnpublishConfirm')}
          warnings={[t('admin.undo.voteUnpublishWarning'), t('admin.undo.noMail')]}
          busy={action.isPending}
          onConfirm={(reason) =>
            action.mutateAsync({ kind: 'vote', id: vote.id, action: 'unpublish', reason })
          }
        />
      ) : steps.length > 0 ? (
        <>
          {vote.status === 'open' ? (
            <CountdownPanel
              eventId={eventId}
              vote={vote}
              remaining={remaining}
              canControl={canVote}
            />
          ) : null}
          {/* La durée se choisit AVANT d'ouvrir : le décompte part avec le
              vote, à la même seconde, sur tous les écrans. */}
          {steps.includes('open') ? (
            <div className="mt-3">
              <DurationPicker
                value={duration}
                onChange={setDuration}
                disabled={action.isPending}
              />
            </div>
          ) : null}
          {/* Cibles larges : on vise avec un pouce, l'autre main tient le micro. */}
          <div className="mt-3 grid grid-cols-2 gap-2">
            {steps.map((step) => (
              <Button
                key={step}
                size="sm"
                variant={
                  step === 'publish'
                    ? 'champagne'
                    : step === 'open' || (elapsed && step === 'freeze')
                      ? 'ember'
                      : 'outline'
                }
                className={cn(step === 'open' && 'col-span-2 min-w-0')}
                disabled={action.isPending}
                onClick={() =>
                  step === 'publish'
                    ? setPublishing(true)
                    : action.mutate({
                        kind: 'vote',
                        id: vote.id,
                        action: step,
                        ...(step === 'open' ? { durationSeconds: duration } : {}),
                      })
                }
              >
                {step === 'open' && duration !== null
                  ? t('voteTimer.openFor', { time: formatClock(duration) })
                  : t(`conductor.contest.voteAction.${step}`)}
              </Button>
            ))}
          </div>
        </>
      ) : vote.status === 'open' ? (
        // Sans le droit de conduire : le temps qui reste, sans les gestes.
        <CountdownPanel eventId={eventId} vote={vote} remaining={remaining} canControl={false} />
      ) : null}

      {empty ? (
        <p className="mt-2 text-caption text-state-warning">{t('conductor.contest.noNominees')}</p>
      ) : null}

      {action.isError ? (
        <p className="mt-2 text-caption text-state-danger">
          {action.error instanceof ApiError ? action.error.message : t('state.errorTitle')}
        </p>
      ) : null}

      {children}
    </div>
  )
}

/**
 * Le défilé, d'un pouce : qui passe maintenant sur le podium.
 *
 * Toucher un nom l'affiche en grand sur l'écran de la salle (photo, numéro,
 * profil) ; « Suivant » passe au candidat d'après, dans l'ordre des dossards ;
 * « Personne » rend l'écran au programme. Le candidat à l'écran est marqué
 * ici : le MC sait ce que la salle regarde sans se retourner vers la télé.
 */
function RunwayControl({ eventId, round }: { eventId: string | undefined; round: ConductorRound }) {
  const { t } = useTranslation()
  const onStage = useOnStage(eventId)
  const current = round.on_stage_candidate_id ?? null
  const index = round.participants.findIndex((entry) => entry.candidate_id === current)
  // Sans personne à l'écran, « Suivant » commence par le premier dossard.
  const next = round.participants[index + 1] ?? null
  const put = (candidateId: string | null) =>
    onStage.mutate({ roundId: round.id, candidateId })

  return (
    <div>
      <p className="text-caption text-content-muted">{t('conductor.contest.runwayHint')}</p>
      <ul className="mt-2 grid grid-cols-2 gap-2">
        {round.participants.map((participant) => {
          const active = participant.candidate_id === current
          return (
            <li key={participant.candidate_id}>
              <button
                type="button"
                aria-pressed={active}
                disabled={onStage.isPending}
                onClick={() => put(active ? null : participant.candidate_id)}
                className={cn(
                  'flex min-h-11 w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors disabled:opacity-60',
                  active
                    ? 'border-ember-400 bg-ember-400/15 text-content-primary'
                    : 'border-line-subtle text-content-secondary hover:border-champagne-400/60',
                )}
              >
                {participant.candidate_number !== null ? (
                  <span className="font-mono text-champagne-300">#{participant.candidate_number}</span>
                ) : null}
                <span className="min-w-0 flex-1 break-words">{participant.name}</span>
                {active ? (
                  <span className="shrink-0 text-caption text-ember-300">
                    {t('conductor.contest.onScreen')}
                  </span>
                ) : null}
              </button>
            </li>
          )
        })}
      </ul>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <Button
          size="sm"
          variant={next ? 'ember' : 'outline'}
          disabled={onStage.isPending || !next}
          onClick={() => next && put(next.candidate_id)}
          className="min-w-0"
        >
          {next
            ? t('conductor.contest.runwayNext', {
                name:
                  next.candidate_number !== null ? `#${next.candidate_number}` : next.name,
              })
            : t('conductor.contest.runwayDone')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={onStage.isPending || current === null}
          onClick={() => put(null)}
        >
          {t('conductor.contest.runwayClear')}
        </Button>
      </div>
      {onStage.isError ? (
        <p className="mt-2 text-caption text-state-danger">
          {onStage.error instanceof ApiError ? onStage.error.message : t('state.errorTitle')}
        </p>
      ) : null}
    </div>
  )
}

/**
 * Les tours d'un concours (ou de tous), et le vote de chacun.
 *
 * `contestId` restreint à un concours (l'onglet « Concours ») ; `runningOnly`
 * ne garde que ce qui est sur scène ; `rounds` choisit les tours à montrer
 * (le « Direct » : ceux de l'étape dont c'est la carte) ; `tiebreaks` dit
 * quels départages suivre — tous, les ouverts seulement (d'office avec
 * `runningOnly`), ou aucun quand un autre bloc s'en charge ; `bare` retire le surtitre quand
 * l'écran appelant a déjà le sien.
 */
export function ContestControl({
  eventId,
  can,
  contestId,
  runningOnly = false,
  rounds: pick,
  tiebreaks: tiebreakScope,
  bare = false,
}: {
  eventId: string | undefined
  /** Les droits du compte connecté : chaque bouton suit le sien. */
  can: (permission: string) => boolean
  contestId?: string
  runningOnly?: boolean
  rounds?: (round: ConductorRound) => boolean
  tiebreaks?: 'all' | 'open' | 'none'
  bare?: boolean
}) {
  const { t, i18n } = useTranslation()
  const canRound = can('competition.round.control')
  const canVote = can('voting.category.control')
  const board = useContestBoard(eventId, true)
  const action = useContestAction(eventId)

  // Sans le droit de piloter le concours, le panneau n'a rien à montrer : un
  // message d'erreur au milieu du déroulé dérouterait plus qu'il n'aiderait.
  if (board.isError || !board.data) return null
  const { contests } = board.data
  const inScope = (id: string | null) => !contestId || id === contestId
  const rounds = board.data.rounds.filter(
    (round) =>
      inScope(round.competition_category_id) &&
      (!runningOnly || round.status === 'running') &&
      (!pick || pick(round)),
  )
  const votes = board.data.votes.filter((vote) => inScope(vote.competition_category_id))
  const scope = tiebreakScope ?? (runningOnly ? 'open' : 'all')
  const tiebreaks =
    scope === 'none'
      ? []
      : votes.filter((vote) => vote.is_tiebreak && (scope === 'all' || vote.status === 'open'))
  if (rounds.length === 0 && tiebreaks.length === 0) return null

  const contestName = (id: string | null) =>
    id ? label(contests.find((entry) => entry.id === id)?.name_i18n ?? {}, i18n.language) : ''

  const voteRow = (vote: ConductorVote) => (
    <VoteControlRow key={vote.id} eventId={eventId} vote={vote} canVote={canVote} />
  )

  const roundCard = (round: ConductorRound) => {
    const ballots = votes.filter(
      (vote) => vote.competition_round_id === round.id && !vote.is_tiebreak,
    )
    return (
      <li key={round.id}>
        <Card
          className={cn(
            'space-y-4 p-4',
            round.status === 'running' ? 'border-2 border-ember-400' : 'border-line-subtle',
            round.status === 'done' && ballots.every((vote) => vote.status === 'published')
              ? 'opacity-60'
              : '',
          )}
        >
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              {round.competition_category_id && !contestId ? (
                <p className="overline text-content-muted">
                  {contestName(round.competition_category_id)}
                </p>
              ) : null}
              <h3 className="font-display text-heading-sm text-content-primary">
                {label(round.name_i18n, i18n.language)}
              </h3>
            </div>
            <Badge tone={ROUND_TONES[round.status]}>
              {t(`conductor.contest.roundStatus.${round.status}`)}
            </Badge>
          </div>

          {/* Un tour terminé se REPREND : fermé trop tôt, il emportait avec
              lui le défilé et les commandes de son vote, sans retour possible. */}
          {canRound ? (
            <Button
              className="w-full"
              size="sm"
              variant={round.status === 'running' ? 'outline' : 'ember'}
              disabled={action.isPending}
              onClick={() =>
                action.mutate({
                  kind: 'round',
                  id: round.id,
                  action: round.status === 'running' ? 'end' : 'start',
                })
              }
            >
              {round.status === 'pending'
                ? t('conductor.contest.roundStart')
                : round.status === 'done'
                  ? t('conductor.contest.roundResume')
                  : t('conductor.contest.roundEnd')}
            </Button>
          ) : null}

          {round.participants.length > 0 ? (
            round.status === 'running' && canRound ? (
              <RunwayControl eventId={eventId} round={round} />
            ) : (
              <div>
                <p className="text-caption text-content-muted">{t('conductor.contest.onStage')}</p>
                <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-content-secondary">
                  {round.participants.map((participant) => (
                    <li key={participant.candidate_id}>
                      {participant.candidate_number !== null ? (
                        <span className="mr-1 font-mono text-champagne-300">
                          #{participant.candidate_number}
                        </span>
                      ) : null}
                      {participant.name}
                    </li>
                  ))}
                </ul>
              </div>
            )
          ) : null}

          {ballots.length > 0 ? (
            <div className="space-y-2">{ballots.map(voteRow)}</div>
          ) : (
            <p className="text-caption text-state-warning">{t('conductor.contest.noVote')}</p>
          )}
        </Card>
      </li>
    )
  }

  return (
    <section className={bare ? '' : 'mt-12'}>
      {bare ? null : (
        <>
          <Overline>{t('conductor.contest.overline')}</Overline>
          <p className="mt-2 max-w-2xl text-sm text-content-secondary">
            {t('conductor.contest.hint')}
          </p>
        </>
      )}

      {action.isError ? (
        <p className="mt-4 text-sm text-state-danger">
          {action.error instanceof ApiError ? action.error.message : t('state.errorTitle')}
        </p>
      ) : null}

      <ol className={cn('grid gap-4 md:grid-cols-2', bare ? '' : 'mt-6')}>
        {rounds.map(roundCard)}
      </ol>

      {/* Le départage se monte dans « Votes » (les cinq QR s'y affichent) ;
          ici, on le conduit comme un tour : ouvrir, geler. Le vainqueur se
          proclame ensuite depuis « Votes ». */}
      {tiebreaks.length > 0 ? (
        <div className="mt-6 space-y-2">
          <p className="overline text-content-muted">{t('conductor.contest.tiebreak')}</p>
          {tiebreaks.map((vote) => (
            <div key={vote.id}>
              {vote.competition_category_id && !contestId ? (
                <p className="mb-1 text-caption text-content-muted">
                  {contestName(vote.competition_category_id)}
                </p>
              ) : null}
              {voteRow(vote)}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  )
}
